import React, { useState, useEffect, useMemo } from "react";
import { Plus, ArrowUpFromLine, Search, Pencil, X, CheckCircle, Printer, Download } from "lucide-react";
import { api } from "../../utils/api";
import * as XLSX from "xlsx";

import IncomeReceipt from "../../components/IncomeReceipt";
import ExpenseReceipt from "../../components/ExpenseReceipt";

const money = n => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmt = d => d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [modal, setModal] = useState(null);
  const [printData, setPrintData] = useState(null); 
  const [siteSettings, setSiteSettings] = useState({ site_name: "Başkent Sitesi", manager1: "" });
  const [owners, setOwners] = useState([]);
  const [currentUser, setCurrentUser] = useState(null); 
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [activePeriod, setActivePeriod] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/auth/me").then(r => setCurrentUser(r.data)).catch(() => {});
    api.get(`/transactions${kind ? `?kind=${kind}` : ""}`).then(r => { setItems(r.data || []); setLoading(false); }).catch(() => setLoading(false));
    api.get("/periods").then(r => setActivePeriod((r.data || []).find(p => p.active || p.is_active))).catch(() => {});
    api.get("/settings").then(r => { if (r.data) setSiteSettings(r.data); }).catch(() => {});
    api.get("/owners").then(r => setOwners(r.data || [])).catch(() => {});
  };

  const isAdmin = currentUser && currentUser.role !== "Denetçi" && currentUser.role !== "Viewer";

  useEffect(() => { load(); }, [kind]);
  useEffect(() => { setPage(1); }, [q, kind]);

  const filtered = useMemo(() => {
    let res = items;
    const query = q.toLowerCase().trim();
    if (query) {
      res = res.filter(x => (x.description || "").toLowerCase().includes(query) || (x.category || "").toLowerCase().includes(query) || (x.owner_code || "").toLowerCase().includes(query) || (x.reference || "").toLowerCase().includes(query) || (x.adi_soyadi || "").toLowerCase().includes(query));
    } else if (activePeriod && activePeriod.start_date && activePeriod.end_date) {
      res = res.filter(x => x.date >= activePeriod.start_date && x.date <= activePeriod.end_date);
    }
    return res.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [items, q, activePeriod]);

  const totalIncome = filtered.filter(x => x.kind === "income").reduce((sum, x) => sum + Number(x.amount || 0), 0);
  const totalExpense = filtered.filter(x => x.kind === "expense").reduce((sum, x) => sum + Number(x.amount || 0), 0);

  const itemsPerPage = 50;
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const visible = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const remove = async (id) => {
    if (window.confirm("Bu kaydı silmek istediğinize emin misiniz? Bakiye hesaplamaları etkilenecektir.")) {
      try { await api.delete(`/transactions/${id}`); load(); } catch (err) { alert("Silme işlemi başarısız oldu."); }
    }
  };

  const handlePrint = (tx) => {
    setPrintData(tx);
    setTimeout(() => { window.print(); }, 200);
  };

  // EXCEL'E AKTARMA FONKSİYONU
  const exportToExcel = () => {
    const incomes = filtered.filter(x => x.kind === "income").map(x => ({
      "Tarih": fmt(x.date),
      "Ödeyen / Referans": x.adi_soyadi || x.reference || x.owner_code || "",
      "Açıklama": x.description,
      "Kategori": x.category,
      "Hesap": x.payment_method,
      "Tutar (TL)": Number(x.amount || 0)
    }));

    const expenses = filtered.filter(x => x.kind === "expense").map(x => ({
      "Tarih": fmt(x.date),
      "Referans": x.reference || "",
      "Açıklama": x.description,
      "Kategori": x.category,
      "Hesap": x.payment_method,
      "Tutar (TL)": Number(x.amount || 0)
    }));

    const wb = XLSX.utils.book_new();
    
    if (incomes.length > 0) {
      const wsIncome = XLSX.utils.json_to_sheet(incomes);
      XLSX.utils.book_append_sheet(wb, wsIncome, "Gelirler");
    }
    
    if (expenses.length > 0) {
      const wsExpense = XLSX.utils.json_to_sheet(expenses);
      XLSX.utils.book_append_sheet(wb, wsExpense, "Giderler");
    }

    if (incomes.length === 0 && expenses.length === 0) {
      alert("Dışa aktarılacak kayıt bulunamadı.");
      return;
    }

    XLSX.writeFile(wb, `${activePeriod ? activePeriod.name : 'Tum'}_Gelir_Gider_Raporu.xlsx`);
  };

  return (
    <div style={{ animation: "fadeIn 0.4s ease-out" }}>
      
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .receipt-print-area, .receipt-print-area * { visibility: visible; }
          .receipt-print-area { position: absolute; left: 0; top: 0; width: 100%; display: flex; justify-content: center; padding-top: 40px; }
          .no-print { display: none !important; }
        }
        @media screen { .receipt-print-area { display: none; } }
        .tx-table-scroll { overflow-x: auto; width: 100%; scrollbar-width: thin; scrollbar-color: #94a3b8 #f1f5f9; }
        .tx-table-scroll::-webkit-scrollbar { height: 10px; }
        .tx-table-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 0 0 16px 16px; }
        .tx-table-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 6px; border: 2px solid #f1f5f9; }
        .tx-table-scroll::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
      `}</style>

      <div className="no-print" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "24px 30px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", marginBottom: "24px", gap: "20px" }}>
        <div>
          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#3b82f6", letterSpacing: "1px", textTransform: "uppercase" }}>FİNANSAL HAREKETLER</span>
          <h2 style={{ margin: "6px 0", fontSize: "22px", color: "#1e293b" }}>Gelir & Gider İşlemleri</h2>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button onClick={exportToExcel} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#10b981", color: "white", border: "none", padding: "12px 20px", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", transition: "0.2s" }}><Download size={18} /> Excel'e Aktar</button>
          {isAdmin && (
            <>
              <button onClick={() => setModal({ kind: "expense" })} style={{ display: "flex", alignItems: "center", gap: "8px", background: "white", color: "#ef4444", border: "1px solid #fecaca", padding: "12px 20px", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", transition: "0.2s" }}><ArrowUpFromLine size={18} /> Gider Çıkışı</button>
              <button onClick={() => setModal({ kind: "income" })} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#3b82f6", color: "white", border: "none", padding: "12px 20px", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", transition: "0.2s", boxShadow: "0 2px 10px rgba(59, 130, 246, 0.3)" }}><Plus size={18} /> Gelir Ekle</button>
            </>
          )}
        </div>
      </div>

      <div className="no-print" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "16px 24px", borderRadius: "16px", border: "1px solid #e2e8f0", marginBottom: "24px", gap: "20px" }}>
        <div style={{ display: "flex", background: "#f1f5f9", padding: "4px", borderRadius: "10px" }}>
          <button onClick={() => setKind("")} style={{ padding: "8px 16px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "600", fontSize: "13px", background: kind === "" ? "white" : "transparent", color: kind === "" ? "#1e293b" : "#64748b" }}>Tümü</button>
          <button onClick={() => setKind("income")} style={{ padding: "8px 16px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "600", fontSize: "13px", background: kind === "income" ? "white" : "transparent", color: kind === "income" ? "#10b981" : "#64748b" }}>Gelirler</button>
          <button onClick={() => setKind("expense")} style={{ padding: "8px 16px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "600", fontSize: "13px", background: kind === "expense" ? "white" : "transparent", color: kind === "expense" ? "#ef4444" : "#64748b" }}>Giderler</button>
        </div>
        <div style={{ flex: 1, minWidth: "250px", display: "flex", alignItems: "center", gap: "10px", background: "#f8fafc", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
          <Search size={18} color="#94a3b8" />
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Açıklama, malik adı, hesap kodu veya dekont ara..." style={{ border: "none", background: "transparent", outline: "none", width: "100%", fontSize: "14px", color: "#1e293b" }} />
        </div>
      </div>

      <div className="no-print" style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "20px", fontSize: "13px", color: "#64748b", alignItems: "center" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px", background: "#f8fafc", padding: "6px 12px", borderRadius: "20px", border: "1px solid #e2e8f0", fontWeight: "600" }}><CheckCircle size={14} color="#3b82f6" /> {q ? "Arama Sonuçları" : (activePeriod ? `${activePeriod.name} Dönemi` : "Tüm Kayıtlar")} ({filtered.length} kayıt)</span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px", background: "#ecfdf5", color: "#047857", padding: "6px 12px", borderRadius: "20px", fontWeight: "bold" }}>Gelir Toplamı: {money(totalIncome)}</span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px", background: "#fef2f2", color: "#be123c", padding: "6px 12px", borderRadius: "20px", fontWeight: "bold" }}>Gider Toplamı: {money(totalExpense)}</span>
      </div>

      {loading ? (
        <div className="no-print" style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>İşlemler yükleniyor...</div>
      ) : (
        <div className="no-print" style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", overflow: "hidden" }}>
          <div className="tx-table-scroll">
            <table style={{ width: "100%", minWidth: "950px", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", whiteSpace: "nowrap", width: "110px" }}>Tarih</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", maxWidth: "320px" }}>Açıklama & Referans</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", whiteSpace: "nowrap", width: "180px" }}>Tür / Kategori</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", whiteSpace: "nowrap", width: "140px" }}>Kasa / Banka</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "right", whiteSpace: "nowrap", width: "140px" }}>Tutar</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "center", whiteSpace: "nowrap", width: "130px" }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(x => (
                  <tr key={x.id} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: "14px 16px", color: "#475569", fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{fmt(x.date)}</td>
                    <td style={{ padding: "14px 16px", maxWidth: "320px" }}>
                      <strong title={x.description} style={{ display: "block", color: "#1e293b", fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.description}</strong>
                      <small title={x.reference || x.adi_soyadi || x.owner_code || ""} style={{ display: "block", color: "#94a3b8", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.reference || x.adi_soyadi || x.owner_code || "Referans Yok"}</small>
                    </td>
                    <td style={{ padding: "14px 16px", maxWidth: "200px" }}>
                      <span style={{ display: "inline-block", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "bold", background: x.kind === "income" ? "#dcfce7" : "#fef2f2", color: x.kind === "income" ? "#16a34a" : "#ef4444", marginBottom: "4px" }}>{x.kind === "income" ? "GELİR" : "GİDER"}</span>
                      <div title={x.category} style={{ fontSize: "13px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.category}</div>
                    </td>
                    <td style={{ padding: "14px 16px", color: "#475569", fontSize: "14px", whiteSpace: "nowrap" }}>{x.payment_method}</td>
                    <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: "bold", fontSize: "15px", color: x.kind === "income" ? "#10b981" : "#ef4444", whiteSpace: "nowrap" }}>{x.kind === "income" ? "+" : "−"} {money(x.amount)}</td>
                    <td style={{ padding: "14px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <div style={{ display: "inline-flex", gap: "6px" }}>
                        <button onClick={() => handlePrint(x)} title="Makbuz Yazdır" style={{ background: "#fef3c7", color: "#d97706", border: "none", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Printer size={16} /></button>
                        {isAdmin && (
                          <>
                            <button onClick={() => setModal({ kind: x.kind, initial: x })} title="Düzenle" style={{ background: "#f1f5f9", color: "#3b82f6", border: "none", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Pencil size={16} /></button>
                            <button onClick={() => remove(x.id)} title="Sil" style={{ background: "#fef2f2", color: "#ef4444", border: "none", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><X size={16} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && <tr><td colSpan="6" style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>Bu kriterlere uygun işlem kaydı bulunmuyor.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="no-print" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", marginTop: "20px" }}>
          <button disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", background: page === 1 ? "#f8fafc" : "white", color: page === 1 ? "#94a3b8" : "#475569", cursor: page === 1 ? "not-allowed" : "pointer", fontWeight: "600" }}>Önceki</button>
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#475569" }}>Sayfa {page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", background: page === totalPages ? "#f8fafc" : "white", color: page === totalPages ? "#94a3b8" : "#475569", cursor: page === totalPages ? "not-allowed" : "pointer", fontWeight: "600" }}>Sonraki</button>
        </div>
      )}

      {modal && <TransactionModal kind={modal.kind} initial={modal.initial} close={() => setModal(null)} done={() => { setModal(null); load(); }} />}

      {printData && (
        <div className="receipt-print-area">
          {printData.kind === "income" 
            ? <IncomeReceipt printData={printData} siteSettings={siteSettings} owners={owners} />
            : <ExpenseReceipt printData={printData} siteSettings={siteSettings} />
          }
        </div>
      )}
    </div>
  );
}

function TransactionModal({ kind, initial, close, done }) {
  const isIncome = kind === "income";
  
  const incomeCategories = ["Aidat ödemesi", "Demirbaş / Ek Ödeme", "Gecikme Tazminatı", "Diğer"];
  const expenseCategories = [
    "740.03 - Site Personel Gideri",
    "740.06 - Vergi ve SGK İşveren Payları",
    "740.04 - Bahçe Traktör Yükleme/Taşıma Gideri",
    "254.01 - Taşıtlar",
    "740.01 - Banka EFT/Havale Giderleri",
    "740.01 - Huzur Hakkı Giderleri",
    "740.01 - Muhasebe Giderleri",
    "740.07 - Noter ve Avukatlık Giderleri",
    "740.01 - İletişim Giderleri",
    "740.01 - Hırdavat Giderleri",
    "740.01 - Bakım Onarım Giderleri",
    "740.02 - Elektrik Gideri",
    "740.01 - İdari Giderler",
    "740.01 - Motorin/Benzin Giderleri",
    "255.01 - Demirbaşlar",
    "740.01 - Kırtasiye Giderleri"
  ];

  const [form, setForm] = useState(
    initial || { 
      date: new Date().toISOString().slice(0, 10), 
      amount: "", 
      category: isIncome ? incomeCategories[0] : expenseCategories[0], 
      payment_method: "", 
      reference: "", 
      description: "", 
      owner_code: "" 
    }
  );
  
  const [owners, setOwners] = useState([]);
  const [ownerBalance, setOwnerBalance] = useState(null); // YENİ: Seçilen kişinin bakiyesini tutar
  
  const [accounts, setAccounts] = useState(
    initial?.payment_method 
      ? Array.from(new Set(["Ziraat Bankası", "İş Bankası", initial.payment_method, "Nakit"])) 
      : ["Ziraat Bankası", "İş Bankası", "Nakit"]
  );

  useEffect(() => {
    api.get("/settings").then(r => { 
      if (r.data && Array.isArray(r.data.banks)) { 
        const bankNames = r.data.banks.map(b => typeof b === 'object' ? b.name : b);
        setAccounts(Array.from(new Set(["Ziraat Bankası", "İş Bankası", ...bankNames, initial?.payment_method, "Nakit"].filter(Boolean)))); 
      } 
    }).catch(() => {});
    
    if (isIncome) {
      api.get("/owners").then(r => {
        let ownerList = r.data || [];
        ownerList.sort((a, b) => {
          const nameA = (a.payer_name || a.owner_name || a.resident_name || "").trim();
          const nameB = (b.payer_name || b.owner_name || b.resident_name || "").trim();
          return nameA.localeCompare(nameB, 'tr');
        });
        setOwners(ownerList);
      }).catch(() => {});

      if (!initial) {
        api.get("/periods").then(r => {
          const act = (r.data || []).find(p => p.active || p.is_active);
          if (act && act.due_1) {
            setForm(prev => ({ ...prev, amount: act.due_1 }));
          }
        }).catch(() => {});
      }
    }
  }, [isIncome, initial]);

  // YENİ: Kişi seçildiğinde güncel bakiyesini anında getir
  useEffect(() => {
    if (isIncome && form.owner_code) {
      api.get(`/statement/${form.owner_code}`)
        .then(res => {
          if (res.data && res.data.totals) {
            setOwnerBalance(res.data.totals.balance);
          }
        })
        .catch(() => setOwnerBalance(null));
    } else {
      setOwnerBalance(null);
    }
  }, [form.owner_code, isIncome]);

  useEffect(() => {
    if (isIncome && form.owner_code && form.date && form.category) {
      const owner = owners.find(o => o.accounting_code === form.owner_code);
      if (owner) {
        const payer = owner.payer_name || owner.owner_name || owner.resident_name || "";
        const dateObj = new Date(form.date);
        
        const monthName = dateObj.toLocaleDateString('tr-TR', { month: 'long' });
        const year = dateObj.getFullYear();
        const ayYil = `${monthName} ${year}`;
        
        const desc = `Gönd: ${payer} ${owner.accounting_code} için ${ayYil} ayı ${form.category}`;
        
        setForm(prev => ({ ...prev, description: desc }));
      }
    }
  }, [form.owner_code, form.date, form.category, isIncome, owners]);

  const change = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async e => {
    e.preventDefault();
    const numAmount = Number(form.amount);
    
    if (numAmount <= 0) {
      alert("Tutar 0 (Sıfır) olamaz. Lütfen geçerli bir tutar giriniz.");
      return;
    }

    if (!form.payment_method) {
      alert("Lütfen bir Ödeme Yöntemi seçiniz.");
      return;
    }

    if (form.payment_method === "Nakit" && form.category === "Aidat ödemesi") {
      const isConfirmed = window.confirm("Aidat ödemesi nakit tahsil edilecek. Emin misiniz?");
      if (!isConfirmed) {
        return; 
      }
    }

    const payload = { ...form, kind, amount: numAmount };
    if (isIncome && payload.owner_code) {
      const selectedOwner = owners.find(o => o.accounting_code === payload.owner_code);
      if (selectedOwner) payload.adi_soyadi = selectedOwner.owner_name || selectedOwner.resident_name;
    }
    try {
      if (initial) await api.put(`/transactions/${initial.id}`, payload);
      else await api.post("/transactions", payload);
      done();
    } catch (x) { alert("Kayıt başarısız oldu."); }
  };

  return (
    <div className="no-print" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", width: "100%", maxWidth: "550px", borderRadius: "20px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", overflow: "hidden", animation: "slideUp 0.3s ease-out" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #e2e8f0", background: isIncome ? "#f0fdf4" : "#fef2f2" }}>
          <div>
            <span style={{ fontSize: "12px", fontWeight: "bold", color: isIncome ? "#16a34a" : "#ef4444", letterSpacing: "1px" }}>{initial ? "KAYIT GÜNCELLE" : "YENİ İŞLEM EKLENİYOR"}</span>
            <h2 style={{ margin: "4px 0 0 0", fontSize: "18px", color: "#1e293b" }}>{isIncome ? "Tahsilat (Gelir) Girişi" : "Ödeme (Gider) Çıkışı"}</h2>
          </div>
          <button onClick={close} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: "4px" }}><X size={24} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: "24px", maxHeight: "75vh", overflowY: "auto" }}>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>İşlem Tarihi</label>
              <input name="date" type="date" lang="tr-TR" value={form.date} onChange={change} required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontFamily: "inherit" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: isIncome ? "#10b981" : "#ef4444", marginBottom: "6px" }}>Tutar (TL)</label>
              <input name="amount" type="number" min="0" step="0.01" value={form.amount} onChange={change} placeholder="0,00" required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: `2px solid ${isIncome ? "#34d399" : "#fca5a5"}`, outline: "none", fontWeight: "bold", fontSize: "16px", color: isIncome ? "#047857" : "#b91c1c" }} />
            </div>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px", background: "#f8fafc", padding: "16px", borderRadius: "12px" }}>
            
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Ödeme Yöntemi</label>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {accounts.map(a => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setForm({ ...form, payment_method: a })}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: form.payment_method === a ? "2px solid #3b82f6" : "1px solid #cbd5e1",
                      background: form.payment_method === a ? "#eff6ff" : "white",
                      color: form.payment_method === a ? "#1d4ed8" : "#475569",
                      fontWeight: form.payment_method === a ? "bold" : "600",
                      cursor: "pointer",
                      flex: a === "Nakit" ? "1 1 100%" : "1",
                      transition: "all 0.2s"
                    }}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>{isIncome ? "Gelir Türü" : "Kategori / Gider Türü"}</label>
              <select name="category" value={form.category} onChange={change} required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", background: "white" }}>
                {isIncome ? incomeCategories.map(c => <option key={c} value={c}>{c}</option>) : expenseCategories.map((c, idx) => <option key={idx} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {isIncome && (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>
                <span>
                  Ödemeyi Yapan Kişi 
                  {form.category === "Diğer" && <span style={{color: "#94a3b8", fontWeight: "normal"}}> (İsteğe Bağlı)</span>}
                </span>
                
                {/* YENİ: Bakiye Göstergesi */}
                {ownerBalance !== null && (
                  <span style={{ 
                    color: ownerBalance > 0 ? "#ef4444" : (ownerBalance < 0 ? "#10b981" : "#64748b"),
                    background: ownerBalance > 0 ? "#fef2f2" : (ownerBalance < 0 ? "#ecfdf5" : "#f1f5f9"),
                    padding: "2px 8px", borderRadius: "8px", fontSize: "12px", fontWeight: "bold", border: "1px solid"
                  }}>
                    {ownerBalance > 0 ? "Borç: " : (ownerBalance < 0 ? "Alacak: " : "Bakiye: ")}
                    {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(Math.abs(ownerBalance))}
                  </span>
                )}
              </label>
              
              <select 
                name="owner_code" 
                value={form.owner_code} 
                onChange={change} 
                required={form.category !== "Diğer"} /* YENİ: Diğer seçildiğinde zorunluluğu kaldır */
                style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", background: "white" }}
              >
                <option value="">— Kişi Seçiniz —</option>
                {owners.map(o => (
                  <option key={o.accounting_code} value={o.accounting_code}>
                    {o.payer_name || o.owner_name || o.resident_name || "(İsimsiz)"} · Paragon {o.villa_no} · {o.accounting_code}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>İşlem Açıklaması</label>
            <input name="description" value={form.description} onChange={change} placeholder={isIncome ? (form.category === "Diğer" ? "Örn: Kumanda satışı" : "Örn: Mayıs ayı aidat ödemesi") : "Örn: Bahçe peyzaj malzemeleri alımı"} required style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontFamily: "inherit" }} />
          </div>
          
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Referans / Evrak No (Opsiyonel)</label>
            <input name="reference" value={form.reference} onChange={change} placeholder="Fatura No, Fiş No veya Dekont Açıklaması" style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontFamily: "inherit" }} />
          </div>
          
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button type="button" onClick={close} style={{ padding: "12px 20px", background: "white", border: "1px solid #cbd5e1", borderRadius: "8px", color: "#475569", fontWeight: "600", cursor: "pointer" }}>Vazgeç</button>
            <button type="submit" style={{ padding: "12px 24px", background: isIncome ? "#3b82f6" : "#ef4444", border: "none", borderRadius: "8px", color: "white", fontWeight: "bold", cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>{initial ? "Güncelle" : "İşlemi Kaydet"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}